import { useMutation, useQuery } from "@apollo/client";
import gql from "graphql-tag";
import * as Cookies from "js-cookie";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import Tooltip from "react-tooltip-lite";
import { handleGraphQLErrors } from "../../../utils/handleGraphQLError";
import { useOutsideClick } from "../../../utils/useOutsideClick";
import { Icon } from "../../Icon/Icon";
import { Button } from "../Button/Button";
import { EditableInformation } from "../EditableInformation/EditableInformation";
import { Modal } from "../Modal/Modal";
import { AddApplicationBaloonContent } from "./AddApplicationBaloonContent/AddApplicationBaloonContent";
import { AddApplicationModalContent } from "./AddApplicationModalContent/AddApplicationModalContent";
import "./ApplicationDisplay.css";
import { ApplicationDisplayItem } from "./ApplicationDisplayItem/ApplicationDisplayItem";

export /**
 *
 *
 * @param {object} props
 * @param {string} props.accountId
 * @param {boolean} props.canManage
 * @param {*} props.data
 * @param {*} props.error
 * @param {boolean} props.loading
 * @param {string} props.distributorId
 * @return {*}
 */
const ApplicationDisplay = ({
  accountId,
  canManage,
  data,
  error,
  loading,
  distributorId,
  accessLevel,
}) => {
  const ref = useRef();

  const { data: currentUserData } = useQuery(
    getCurrentUserAccountInformationQuery,
    {
      variables: {
        id: Cookies.get("primary_account"),
      },
      pollInterval: 30000,
    }
  );

  const { t } = useTranslation();

  const [addApplicationDisplay, setAddApplicationDisplay] = useState(false);
  const [addApplicationBaloon, setAddApplicationBaloon] = useState(false);
  const [selectedApp, setSelectedApp] = useState(null);
  const [appId, setAppId] = useState("");
  const [appName, setAppName] = useState("");
  const [addApplication] = useMutation(addApplicationMutation);

  useOutsideClick(ref, () => setAddApplicationBaloon(false));

  /** @type {*} */
  const params = useParams();
  const [workflowApps, setWorkflowApps] = useState([]);
  const [inventoryApps, setInventoryApps] = useState([]);

  useEffect(() => {
    if (data) {
      setWorkflowApps(data.apps.filter((x) => x.type === "WORKFLOW"));
      setInventoryApps(data.apps.filter((x) => x.type === "INVENTORY"));
    }
  }, [data]);

  useEffect(() => {
    if (error) {
      handleGraphQLErrors(error);
    }
  }, [error]);

  return (
    <div className="ApplicationDisplay">
      {addApplicationDisplay && selectedApp && (
        <Modal
          title={
            selectedApp === "WORKFLOW"
              ? t("Add Workflow application")
              : selectedApp === "INVENTORY"
              ? t("Add Inventory application")
              : t("Add application")
          }
          content={
            <AddApplicationModalContent
              appId={appId}
              setAppId={setAppId}
              appName={appName}
              setAppName={setAppName}
              app={selectedApp}
              distributorId={distributorId}
              accessLevel={accessLevel}
            />
          }
          reject={() => {
            setSelectedApp(null);
            setAddApplicationDisplay(false);
          }}
          accept={async () => {
            await addApplication({
              variables: {
                accountId: params.id,
                appId,
                appName,
                appType: selectedApp,
              },
            });
            setSelectedApp(null);
            setAppId("");
            setAppName("");
            setAddApplicationDisplay(false);
          }}
          acceptText={t("Add application")}
        />
      )}
      <EditableInformation title="sectionTitles.applications">
        {currentUserData?.account?.allowedApps && canManage && (
          <div className="ApplicationDisplay__add" ref={ref}>
            <Tooltip
              className="ApplicationDisplay__tooltip"
              padding={0}
              distance={0}
              arrow={false}
              isOpen={addApplicationBaloon}
              content={
                <AddApplicationBaloonContent
                  setSelectedApp={setSelectedApp}
                  setAddApplicationDisplay={setAddApplicationDisplay}
                  setAddApplicationBaloon={setAddApplicationBaloon}
                  allowedApps={currentUserData?.account?.allowedApps}
                />
              }
            >
              <Button
                buttonStyle="Button__external ApplicationDisplay__button"
                active={addApplicationBaloon}
                click={(e) => {
                  e.preventDefault();
                  setAddApplicationBaloon(!addApplicationBaloon);
                }}
              >
                <div className="ApplicationDisplay__icon">
                  <Icon name="iconExternal" width={12} height={16} />
                </div>
                <div className="ApplicationDisplay__text">
                  {t("Add application")}
                </div>
              </Button>
            </Tooltip>
          </div>
        )}
        {loading && <div>Loading apps...</div>}
        {!loading && data && data.apps.length === 0 && (
          <div className="ApplicationDisplay__no-apps">
            {t("This account has no associated apps")}
          </div>
        )}
        {!loading && data && workflowApps.length > 0 && (
          <ApplicationsHeader app="WORKFLOW">
            {workflowApps.map((app) => (
              <ApplicationDisplayItem
                app={app}
                key={app.id}
                accountId={accountId}
                canManage={canManage}
                distributorId={distributorId}
              />
            ))}
          </ApplicationsHeader>
        )}
        {!loading && data && inventoryApps.length > 0 && (
          <ApplicationsHeader app="INVENTORY">
            {inventoryApps.map((app) => (
              <ApplicationDisplayItem
                app={app}
                key={app.id}
                accountId={accountId}
                canManage={canManage}
                distributorId={distributorId}
              />
            ))}
          </ApplicationsHeader>
        )}
      </EditableInformation>
    </div>
  );
};

export const ApplicationsHeader = ({ app, children }) => {
  const selectAppIcon = () => {
    switch (app) {
      case "WORKFLOW":
        return "iconWorkflow";

      case "INVENTORY":
        return "iconInventory";

      default:
        return "iconCross";
    }
  };

  return (
    <>
      <div className="ApplicationsHeader">
        <div className="ApplicationsHeader__icon">
          <Icon name={selectAppIcon()} width={30} height={30} />
        </div>
        <div className="ApplicationsHeader__title">{app.toLowerCase()}</div>
      </div>
      {children}
    </>
  );
};

const addApplicationMutation = gql`
  mutation addNewApplication(
    $userId: String
    $accountId: String
    $appId: String
    $appName: String
    $appType: String
  ) {
    addNewApplication(
      userId: $userId
      accountId: $accountId
      appId: $appId
      appName: $appName
      appType: $appType
    )
  }
`;

const getCurrentUserAccountInformationQuery = gql`
  query getCurrentUserAccountInformation($id: String) {
    account(id: $id) {
      id
      name
      email
      allowedApps
    }
  }
`;
